import React, { useState, useEffect } from 'react';
import { strategistService, type StrategistSettings } from '@/services/strategistService';

export const ConfigStrategist: React.FC = () => {
  const [settings, setSettings] = useState<StrategistSettings>({
    enabled: true,
    ai_model: 'gpt-4.1-mini',
    temperature: 0.3,
    lead_time_sensitivity: 'padrao',
    auto_create_board_cards: false,
    affiliate_amazon_tag: '',
    affiliate_shopee_id: '',
    affiliate_booking_url: '',
    revenue_target_monthly_rs: 50000.0
  });

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [statusMsg, setStatusMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const fetchSettings = async () => {
    setLoading(true);
    try {
      const data = await strategistService.getSettings();
      setSettings(data);
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: 'Falha ao carregar configurações do Estrategista.' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSettings();
  }, []);

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setStatusMsg(null);
    try {
      const res = await strategistService.updateSettings(settings);
      setSettings(res.settings);
      setStatusMsg({ type: 'success', text: 'Configurações salvas com sucesso.' });
      setTimeout(() => setStatusMsg(null), 4000);
    } catch (err: any) {
      setStatusMsg({ type: 'error', text: err?.message || 'Erro ao salvar configurações.' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: '40px', color: 'var(--text-3)' }}>Carregando configurações...</div>;
  }

  return (
    <div style={{ padding: '24px 32px', maxWidth: '1000px', margin: '0 auto', color: 'var(--text-1)' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', borderBottom: '1px solid var(--border-1)', paddingBottom: '16px' }}>
        <div>
          <h1 style={{ fontSize: '20px', fontWeight: 800, margin: 0, color: 'var(--text-1)', letterSpacing: '-0.02em' }}>
            Configuração do Estrategista Inteligente
          </h1>
          <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-3)', margin: '4px 0 0 0' }}>
            Identificadores de afiliação usados pelas sugestões do Estrategista.
          </p>
        </div>
        <a
          href="/inteligencia/playbook"
          style={{
            padding: '8px 14px',
            backgroundColor: 'var(--bg-1)',
            color: 'var(--text-1)',
            border: '1px solid var(--border-2)',
            borderRadius: 'var(--r-md)',
            textDecoration: 'none',
            fontSize: 'var(--fs-sm)',
            fontWeight: 600
          }}
        >
          ← Voltar ao Playbook
        </a>
      </div>

      {statusMsg && (
        <div style={{
          backgroundColor: statusMsg.type === 'success' ? 'var(--sig-ok-bg)' : 'var(--sig-crit-bg)',
          border: `1px solid ${statusMsg.type === 'success' ? 'var(--sig-ok-border)' : 'var(--sig-crit-border)'}`,
          color: statusMsg.type === 'success' ? 'var(--sig-ok-text)' : 'var(--sig-crit-text)',
          padding: '12px 16px',
          borderRadius: 'var(--r-md)',
          fontSize: 'var(--fs-sm)',
          fontWeight: 600,
          marginBottom: '24px'
        }}>
          {statusMsg.text}
        </div>
      )}

      {/* AVISO UNICO NO LUGAR DOS CONTROLES MORTOS.
          Saiam da tela seis controles cujo valor nao tem UM leitor no produto:
          `enabled`, `ai_model`, `temperature` (strategist_service.py:16,19,20 —
          process_chat, 221-378, nunca os consulta e nao chama adaptador de IA
          nenhum), `lead_time_sensitivity` (o endpoint chama get_radar_items
          sem passar `sensitivity`), `auto_create_board_cards` e
          `revenue_target_monthly_rs` (gravados no dict de memoria e devolvidos
          para esta mesma tela — nenhum worker, servico ou agendador le).
          O ESTADO continua completo: o PUT /strategist/settings segue enviando
          todos os campos, entao nada que ja estava gravado se perde. Quando o
          motor existir, os controles voltam junto com quem os le. */}
      <div style={{
        backgroundColor: 'var(--sig-warn-bg)',
        border: '1px solid var(--sig-warn-border)',
        borderRadius: 'var(--r-lg)',
        padding: '16px 18px',
        marginBottom: '20px'
      }}>
        <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 800, color: 'var(--sig-warn-text)', marginBottom: '6px' }}>
          Motor prescritivo automático — em construção
        </div>
        <p style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-2)', margin: 0, lineHeight: 1.5 }}>
          O motor prescritivo automático está em construção — hoje o Estrategista
          opera pelo Blueprint diário e pelo Playbook, manualmente.
        </p>
        <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-3)', margin: '10px 0 0 0', lineHeight: 1.5 }}>
          Por isso saíram desta tela os controles que ninguém lia: habilitar o
          copiloto global, modelo de LLM e temperatura, sensibilidade de lead
          time (antecipação), criação automática no Kanban e meta mensal de
          comissões. O que já estava gravado continua guardado — os controles
          voltam quando existir motor que os leia.
        </p>
      </div>

      <form onSubmit={handleSave}>
        {/* Identificadores de afiliacao — ROTULADOS, nao escondidos: sao o
            insumo que o motor de monetizacao vai precisar, e guardar a tag
            agora e util. O que nao pode e a tela sugerir que ja rendem. */}
        <div style={{ backgroundColor: 'var(--bg-1)', border: '1px solid var(--border-1)', borderRadius: 'var(--r-lg)', padding: '24px', marginBottom: '24px' }}>
          <h2 style={{ fontSize: '15px', fontWeight: 800, color: 'var(--text-1)', margin: '0 0 8px 0', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
            Identificadores de Afiliação
          </h2>
          <p style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-3)', margin: '0 0 16px 0', lineHeight: 1.5, borderBottom: '1px solid var(--border-1)', paddingBottom: '12px' }}>
            Ainda não conectado — os identificadores ficam registrados aqui, mas
            nenhuma matéria injeta link de afiliado. A única caixa que o produto
            monta hoje é a prévia do Calendário Preditivo, e ela sai com link sem
            tag, sem comissão (em construção).
          </p>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase' }}>Amazon Associates (Tag)</label>
              <input
                type="text"
                value={settings.affiliate_amazon_tag}
                onChange={(e) => setSettings({ ...settings, affiliate_amazon_tag: e.target.value })}
                placeholder="Ex: seu-site-20"
                style={{ backgroundColor: 'var(--bg-2)', border: '1px solid var(--border-2)', color: 'var(--text-1)', padding: '10px 12px', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)', outline: 'none' }}
              />
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              <label style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase' }}>Shopee Affiliate ID</label>
              <input
                type="text"
                value={settings.affiliate_shopee_id}
                onChange={(e) => setSettings({ ...settings, affiliate_shopee_id: e.target.value })}
                style={{ backgroundColor: 'var(--bg-2)', border: '1px solid var(--border-2)', color: 'var(--text-1)', padding: '10px 12px', borderRadius: 'var(--r-md)', fontSize: 'var(--fs-sm)', outline: 'none' }}
              />
            </div>
          </div>
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', paddingTop: '16px', borderTop: '1px solid var(--border-1)' }}>
          <button
            type="submit"
            disabled={saving}
            style={{
              padding: '10px 24px',
              backgroundColor: 'var(--nexus-brand)',
              color: 'var(--text-on-accent)',
              border: 'none',
              borderRadius: 'var(--r-md)',
              fontSize: 'var(--fs-sm)',
              fontWeight: 700,
              cursor: saving ? 'not-allowed' : 'pointer',
              opacity: saving ? 0.7 : 1,
              boxShadow: 'var(--shadow-1)'
            }}
          >
            {saving ? 'Salvando...' : 'Salvar identificadores'}
          </button>
        </div>
      </form>
    </div>
  );
};
